import React from "react";
import { IForm } from "../../../../types/form.type";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";
import { useAlertContext } from "../../../../contexts/alert/AlertProvider";
import { Controller, useForm } from "react-hook-form";
import { asyncPostData, asyncPutData } from "../../../../utils/httpRequests";
import ModalForm from "../../modal/ModalForm";
import { Avatar, Box, MenuItem, Stack, Typography } from "@mui/material";
import TextInput from "../../input/TextInput";
import SelectApiInput from "../../input/SelectApiInput";
import {
  formatDateDisplay,
  generateLinkImage,
} from "../../../../utils/helpers";

const schema = yup.object({
  title: yup.string().required("Vui lòng nhập tiêu đề"),
  content: yup.string().required("Vui lòng nhập nội dung"),
  post: yup
    .object()
    .typeError("Vui lòng chọn bài viết")
    .required("Vui lòng chọn bài viết"),
  trang_thai: yup
    .object()
    .typeError("Vui lòng chọn trạng thái")
    .required("Vui lòng chọn trạng thái"),
});

function FormReportList({
  defaultValues,
  isEdit,
  apiCode,
  open,
  onClose,
  setLoad,
}: IForm) {
  const app = useSelector((state: any) => state.app.data);
  const token = useSelector((state: any) => state.auth.token);
  const { showAlert } = useAlertContext();
  const defaultData: { [key: string]: any } = {
    title: "",
    content: "",
    post: null,
    dien_giai: '',
    trang_thai: null,
  };
  const originData = defaultValues
    ? {
        ...defaultData,
        ...defaultValues,
        dien_giai: defaultValues?.exfields?.dien_giai || '',
        post: defaultValues?.postid
          ? {
              _id: defaultValues?.postid,
            }
          : null,
        trang_thai: defaultValues?.trang_thai
          ? {
              ma_trang_thai: defaultValues?.trang_thai,
              ten_trang_thai: defaultValues?.ten_trang_thai,
            }
          : null,
      }
    : null;

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { isSubmitting, errors },
  } = useForm({
    mode: "onSubmit",
    defaultValues: originData,
    resolver: yupResolver(schema),
  });

  const handleReset = () => {
    reset(originData);
  };

  const generateDataPost = async (values: typeof defaultData) => {
    const { post, trang_thai, dien_giai, ...fields } = values;
    return {
      ...fields,
      postid: post?._id || "",
      trang_thai: trang_thai?.ma_trang_thai || "",
      exfields: {
        ...defaultValues?.exfields,
        dien_giai
      }
    };
  };

  const handleSave = async (values: typeof defaultData) => {
    const dataPost: { [key: string]: any } = await generateDataPost(values);
    let resp = null;
    if (isEdit) {
      resp = await asyncPutData({
        id: dataPost._id,
        apiCode,
        token,
        idApp: app?._id,
        data: dataPost,
      });
    } else {
      resp = await asyncPostData({
        apiCode,
        token,
        idApp: app?._id,
        data: dataPost,
      });
    }
    if (resp.status !== 200) {
      showAlert({
        type: "error",
        message:
          resp?.data?.error || resp?.data?.message || "Lỗi khi lưu đối tượng",
      });
    } else {
      onClose();
      setLoad?.((prev: number) => prev + 1);
    }
  };

  const onSubmit = handleSubmit(handleSave);

  return (
    <ModalForm
      formWidth="500px"
      formTitle="tố cáo bài viết"
      loading={isSubmitting}
      open={open}
      isEdit={isEdit}
      onClose={onClose}
      onSubmit={onSubmit}
      onReset={handleReset}
    >
      <Stack gap={2}>
        <TextInput
          required
          label="Tiêu đề"
          labelWidth="25%"
          placeholder="Nhập tiêu đề"
          name="title"
          register={register}
          errorMessage={errors?.title?.message as string}
        />
        <TextInput
          required
          multiline
          minRows={3}
          maxRows={5}
          label="Nội dung"
          labelWidth="25%"
          placeholder="Nhập tiêu đề"
          name="content"
          register={register}
          errorMessage={errors?.content?.message as string}
        />
        <Controller
          name="post"
          control={control}
          render={({ field: { value, onChange } }) => {
            return (
              <SelectApiInput
                labelWidth="25%"
                label="Bài viết"
                placeholder="Chọn bài viết"
                apiCode="posts"
                conditionGetOrigin={{ _id: value?._id }}
                searchFields={["content", "user_created", "name_user_created"]}
                getOptionLabel={(option) => option?.name_user_created}
                checkEqual={(value, option) => value?._id === option?._id}
                renderOption={(option, { isEqual, key, onClick }) => {
                  return (
                    <MenuItem
                      key={key}
                      onClick={onClick}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        marginBottom: "5px",
                        backgroundColor: isEqual
                          ? "primary.500"
                          : "background.default",
                        borderRadius: "10px",
                      }}
                    >
                      <Stack direction="row" gap={1}>
                        <Avatar
                          src={
                            option?.avatar
                              ? generateLinkImage(option?.avatar)
                              : ""
                          }
                          sx={{ width: "30px", height: "30px" }}
                        />
                        <Stack>
                          <Typography sx={{ fontWeight: 600 }}>
                            {option?.name_user_created}
                          </Typography>
                          <Box
                            className="two-lines-ellipsis"
                            dangerouslySetInnerHTML={{
                              __html: option?.description,
                            }}
                            sx={{ whiteSpace: "wrap" }}
                          ></Box>
                          <Typography
                            sx={{ fontSize: "12px", fontStyle: "italic" }}
                          >
                            Ngày:{" "}
                            {formatDateDisplay(
                              option?.date_created,
                              "HH:mm DD/MM/YYYY"
                            )}
                          </Typography>
                        </Stack>
                      </Stack>
                    </MenuItem>
                  );
                }}
                value={value}
                onChange={onChange}
              />
            );
          }}
        />
        <Controller
          name="trang_thai"
          control={control}
          render={({ field: { value, onChange } }) => {
            return (
              <SelectApiInput
                labelWidth="25%"
                label="Trạng thái"
                placeholder="Chọn trạng thái"
                apiCode="trangthai"
                withIdApp={false}
                defaultCondition={{ma_ct: 'rp_dm'}}
                searchFields={["ma_trang_thai", "ten_trang_thai"]}
                getOptionLabel={(option) => option?.ten_trang_thai}
                checkEqual={(value, option) =>
                  value?.ma_trang_thai === option?.ma_trang_thai
                }
                value={value}
                onChange={onChange}
              />
            );
          }}
        />
         <TextInput
          multiline
          minRows={3}
          maxRows={5}
          label="Ghi chú"
          labelWidth="25%"
          placeholder="Nhập ghi chú"
          name="dien_giai"
          register={register}
        />
      </Stack>
    </ModalForm>
  );
}

export default FormReportList;
